import React, { useMemo } from 'react';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  ColDefBuilder,
  DataTable,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Stack,
  Tag,
  Text,
  makeDefaultSortableColumnDef,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';

import { ArtifactHeader } from './ArtifactHeader';

const diagnosticReportFragment = graphql(`
  fragment DiagnosticReport on DiagnosticReports @_unmask {
    __typename
    id
    observations(orderBy: { ordinal: ASC }) {
      __typename
      id
      name
      value
      unit
      referenceRange
      isOutsideReferenceRange
    }
  }
`);

export const patientDiagnosticReportsFragment = graphql(
  `
    fragment PatientDiagnosticReports on Patients @_unmask {
      __typename
      id
      diagnostic_reports(orderBy: { issuedAt: DESC }) {
        __typename
        id
        name
        issuedAt
        conclusion
        ...DiagnosticReport
      }
    }
  `,
  [diagnosticReportFragment],
);

type ReportRow = {
  id: string;
  name: string;
  issuedAt: string;
  outsideRangeCount: number;
  observationsCount?: number;
};

type ObservationRow = {
  id: string;
  name: string;
  value: string;
  referenceRange: string;
  isOutsideReferenceRange: boolean;
};

const DiagnosticReportDetailsModal: React.FC<{
  name: string;
  diagnosticReportId: string;
}> = ({ diagnosticReportId, name }) => {
  const { t } = useTranslation();
  const { observations = [] } = useCompleteFragment({
    fragment: diagnosticReportFragment,
    key: { id: diagnosticReportId },
  });

  const observationColumns = useMemo(
    () =>
      new ColDefBuilder<ObservationRow>()
        .defaultSortable('name')
        .colDef({
          ...makeDefaultSortableColumnDef(
            'value',
            t('patients.labResults.value'),
          ),
          cell: ({
            row: {
              original: { value, isOutsideReferenceRange },
            },
          }) => {
            if (isOutsideReferenceRange) {
              return <Tag label={value} color="orange" />;
            }

            return (
              <span className="text-sm font-medium text-text-primary">
                {value}
              </span>
            );
          },
        })
        .defaultSortable(
          'referenceRange',
          t('patients.labResults.referenceRange'),
        )
        .build(),
    [t],
  );

  const rows = (observations ?? []).map<ObservationRow>((observation) => ({
    id: observation.id,
    name: observation.name ?? '',
    value: `${observation.value} ${observation.unit ?? ''}`,
    referenceRange: observation.referenceRange ?? '',
    isOutsideReferenceRange: observation.isOutsideReferenceRange ?? false,
  }));

  return (
    <Dialog>
      <DialogTrigger>
        <Text>{name}</Text>
      </DialogTrigger>
      <DialogContent size="4xl" className="max-h-[90vh] overflow-scroll">
        <DialogHeader>
          <DialogTitle>{name}</DialogTitle>
        </DialogHeader>
        <div className="overflow-scroll">
          <DataTable
            columns={observationColumns}
            data={rows}
            isPaginated={false}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export interface PatientLabResultsProps {
  patientId: string;
}

export const PatientLabResults: React.FC<PatientLabResultsProps> = ({
  patientId,
}) => {
  const { t } = useTranslation();
  const { diagnostic_reports } = useCompleteFragment({
    fragment: patientDiagnosticReportsFragment,
    key: { id: patientId },
  });

  const reportColumns = useMemo(
    () =>
      new ColDefBuilder<ReportRow>()
        .colDef({
          ...makeDefaultSortableColumnDef(
            'name',
            t('patients.labResults.name'),
          ),
          id: 'name',
          cell: ({ row }) => (
            <DiagnosticReportDetailsModal
              name={row.original.name}
              diagnosticReportId={row.original.id}
            />
          ),
        })
        .defaultSortable(
          'observationsCount',
          t('patients.labResults.observationsCount'),
        )
        .colDef({
          ...makeDefaultSortableColumnDef(
            'outsideRangeCount',
            t('patients.labResults.outsideReferenceRange'),
          ),
          cell: ({ row }) => {
            const { outsideRangeCount } = row.original;

            if (outsideRangeCount > 0) {
              return (
                <Tag label={outsideRangeCount.toString()} color="orange" />
              );
            }

            return <span>--</span>;
          },
        })
        .dateSortable('issuedAt', t('patients.labResults.issuedAt'))
        .build(),
    [t],
  );

  const rows = (diagnostic_reports ?? []).map<ReportRow>((report) => ({
    id: report.id,
    name: report.name ?? '',
    issuedAt: report.issuedAt ?? '',
    observationsCount: report?.observations?.length ?? 0,
    outsideRangeCount:
      report?.observations?.filter((o) => o.isOutsideReferenceRange)?.length ??
      0,
  }));

  return (
    <Stack gap={3}>
      <ArtifactHeader text={t('patients.labResults.header')} />
      <DataTable
        columns={reportColumns}
        data={rows}
        initialSortingState={[
          {
            id: 'issuedAt',
            desc: true,
          },
        ]}
      />
    </Stack>
  );
};
